// The 4×6 sticker label designs (Classic, Price-first, Minimal, Retail) and their sizes. Ported unchanged from
// type1-sticker-4x6.html: the parity suite compares every exported label pixel for pixel with the old app's.
import type { DrawOptions, LabelData, Orientation, PageDims, Scale, TypeSizes } from '../model/types';
import { _C39, _hasSlot, qrAbs, fmt$, TITLE_STEPS, saleBadge } from './common';

export const STICKER_W = 384;  // 4 inch
export const STICKER_H = 576;  // 6 inch
export const MARGIN    = 14;   // ~0.15" safe margin (sticker printer edge tolerance)
export const GAP       = 8;    // gap between labels when 2-up (cut guide space)
export const LAYOUT_GRID: Record<string, [number, number]> = { '1':[1,1], '2v':[1,2], '2h':[2,1] };

function code39Barcode(rawText: string, maxW: number, barH: number): string {
  if(!rawText) return '';
  // Strip to valid Code 39 chars (uppercase)
  // eslint-disable-next-line no-useless-escape -- kept character for character as the original page had it
  const clean = rawText.toUpperCase().replace(/[^0-9A-Z\-\.\s\$\/\+\%]/g,'');
  if(!clean) return '';
  const encoded = '*' + clean + '*';

  // N = narrow unit in px, W = wide unit = 3N
  // Total units per char = 15N; inter-char gap = 1N
  // N is fractional so the code always fits the space given. Floor of
  // 0.7px ≈ 7.3 mil at 96 PPI — at/above the Code 39 scannable minimum.
  // Ceiling of 3px keeps very wide labels from looking absurd.
  const nChars = encoded.length;
  const totalUnits = nChars * 15 + (nChars - 1);
  const N = Math.min(3, Math.max(0.7, maxW / totalUnits));
  const W = N * 3;

  const els = [];
  let totalW = 0;
  for(let ci = 0; ci < encoded.length; ci++){
    if(ci > 0){ els.push({bar:false, w:N}); totalW += N; }   // inter-char gap
    const pat = _C39[encoded[ci]!] || _C39['0']!;
    for(let i = 0; i < 9; i++){
      const wide = pat[i] === '1';
      const bar  = i % 2 === 0;   // 0,2,4,6,8 are bars; 1,3,5,7 are spaces
      const w = wide ? W : N;
      els.push({bar, w});
      totalW += w;
    }
  }

  const divs = els.map(e =>
    `<div style="width:${e.w.toFixed(2)}px;height:${barH}px;background:${e.bar?'#000':'#fff'};flex-shrink:0;"></div>`
  ).join('');
  return `<div style="display:flex;overflow:hidden;width:${Math.ceil(totalW)}px;height:${barH}px;">${divs}</div>`;
}

function extraRow(k1: string, v1: string, k2: string, v2: string, fs: number, p: number, FO: string): string {
  if(!k1&&!k2) return '';
  const items = [];
  if(k1&&v1) items.push(`<span style="font-family:${FO};font-size:${fs}px;color:#222;font-weight:700;text-transform:uppercase;letter-spacing:1px;">${k1}: </span><span style="font-family:${FO};font-size:${fs}px;font-weight:400;">${v1}</span>`);
  if(k2&&v2) items.push(`<span style="font-family:${FO};font-size:${fs}px;color:#222;font-weight:700;text-transform:uppercase;letter-spacing:1px;">${k2}: </span><span style="font-family:${FO};font-size:${fs}px;font-weight:400;">${v2}</span>`);
  return `<div style="margin-top:${Math.round(3*p/10)}px;display:flex;gap:${p}px;flex-wrap:wrap;">${items.join('')}</div>`;
}

function priceBlock(v: LabelData, s: number, pxSize: number, FC: string, FO: string): string {
  if(!v.price) return '';
  const cmpSize = Math.max(9, Math.round(pxSize * 0.57));
  const compareHtml = v.compare
    ? `<span style="font-family:${FO};font-size:${cmpSize}px;font-weight:500;color:#333;text-decoration:line-through;letter-spacing:0;margin-left:${Math.round(4*s)}px;vertical-align:middle;">${fmt$(v.compare)}</span>`
    : '';
  return `${saleBadge(v, saleFs(pxSize), s, FC)}<span style="font-family:${FC};font-size:${pxSize}px;font-weight:400;font-style:normal;letter-spacing:-1px;line-height:1;">${fmt$(v.price)}</span>${compareHtml}`;
}

const saleFs = (pricePx: number) => Math.max(10, Math.round(pricePx * 0.2));

function titleSize(name: string, base: number, ceil: number, floor: number): number {
  const len = (name || '').length;
  const k = TITLE_STEPS.find(([max]) => len <= max)![1];
  return Math.round(Math.min(ceil - 1, Math.max(floor + 1, base * k)));
}
function nameSizeFor(v: LabelData, C: TypeSizes): number {
  return titleSize(v.name, C.name, Math.max(9, Math.round(C.price * 0.57)),
                   Math.max(C.desc, C.extra, C.sku, C.store, saleFs(C.price)));
}

function tmplClassic(v: LabelData, px: number, py: number, s: number, f: Scale, p: number, qrSrc: string | null, qrPx: number, FC: string, FO: string, barcodeW: number, C: TypeSizes): string {
  const nameFs = nameSizeFor(v, C);
  const headerH = Math.max(16, Math.round(20*s));
  const footerH = Math.max(13, Math.round(18*s));
  const barH    = Math.round(24*s);
  const bodyT   = headerH;
  const bodyB   = footerH;
  const slot    = _hasSlot(qrSrc, v.direction);

  return `<div style="position:relative;width:${px}px;height:${py}px;background:#fff;color:#000;font-family:${FO};border:2px solid #000;box-sizing:border-box;overflow:hidden;">

    <div style="position:absolute;top:0;left:0;right:0;height:${headerH}px;background:#000;color:#fff;display:flex;align-items:center;justify-content:space-between;padding:0 ${p}px;">
      <span style="font-family:${FC};font-size:${C.store}px;font-weight:400;font-style:normal;letter-spacing:${Math.round(2*s)}px;text-transform:uppercase;">${v.store||'STORE NAME'}</span>
      ${v.sku?`<span style="font-family:${FC};font-size:${Math.max(8,C.store-2)}px;font-weight:400;font-style:normal;opacity:.7;letter-spacing:1px;">SKU: ${v.sku}</span>`:''}
    </div>

    <div style="position:absolute;top:${bodyT}px;left:0;right:0;bottom:${bodyB}px;display:flex;align-items:stretch;gap:${p}px;padding:${p}px;overflow:hidden;">
      <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:space-between;gap:${Math.round(6*s)}px;overflow:hidden;">
        <div data-fit style="overflow:hidden;">
          <div data-title style="font-family:${FC};font-size:${nameFs}px;font-weight:400;font-style:normal;line-height:1.2;overflow-wrap:anywhere;word-break:break-word;margin-bottom:${Math.round(2*s)}px;">${v.name||'Product Name'}</div>
          ${C.showDesc && v.desc?`<div data-desc style="font-family:open-sans,sans-serif;font-size:${C.desc}px;color:#222;font-weight:600;line-height:1.45;">${v.desc}</div>`:''}
          ${C.showExtra ? extraRow(v.ex1k,v.ex1v,v.ex2k,v.ex2v,C.extra,p,FO) : ''}
        </div>
        <div>
          ${v.price?`<div style="display:flex;align-items:baseline;flex-wrap:wrap;gap:2px;line-height:1;margin-bottom:${Math.round(2*s)}px;">${priceBlock(v,s,C.price,FC,FO)}</div>`:''}
          ${v.sku?code39Barcode(v.sku,barcodeW,barH):''}
          ${v.sku?`<div style="font-family:${FC};font-size:${C.sku}px;font-weight:400;font-style:normal;letter-spacing:${Math.round(2*s)}px;color:#222;margin-top:2px;">${v.sku.split('').join(' ')}</div>`:''}
        </div>
      </div>
      ${slot?`<div style="flex-shrink:0;display:flex;flex-direction:column;justify-content:flex-end;">${qrAbs(qrSrc,qrPx,FO,v.direction)}</div>`:''}
    </div>

    <div style="position:absolute;bottom:0;left:0;right:0;height:${footerH}px;background:#f0f0f0;border-top:2px solid #000;display:flex;align-items:center;justify-content:space-between;padding:0 ${p}px;">
      <span style="font-family:${FC};font-size:${Math.max(7,C.store-3)}px;font-weight:400;font-style:normal;color:#888;letter-spacing:1px;text-transform:uppercase;">${v.store||''}</span>
      <span style="font-family:${FO};font-size:${f(8)}px;color:#888;letter-spacing:1px;">${new Date().toLocaleDateString()}</span>
    </div>

  </div>`;
}

function tmplPrice(v: LabelData, px: number, py: number, s: number, _f: Scale, p: number, qrSrc: string | null, qrPx: number, FC: string, FO: string, barcodeW: number, C: TypeSizes): string {
  const nameFs = nameSizeFor(v, C);
  const stripeW = Math.round(6*s);
  const slot    = _hasSlot(qrSrc, v.direction);
  const innerL  = stripeW + p;
  const barH    = Math.round(22*s);

  return `<div style="position:relative;width:${px}px;height:${py}px;background:#fff;color:#000;font-family:${FO};border:2px solid #000;box-sizing:border-box;overflow:hidden;">

    <div style="position:absolute;top:0;left:0;width:${stripeW}px;bottom:0;background:#000;"></div>

    <div style="position:absolute;top:0;left:${innerL}px;right:${p}px;bottom:0;display:flex;align-items:stretch;gap:${p}px;padding:${p}px 0;overflow:hidden;">

      <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:space-between;gap:${Math.round(6*s)}px;overflow:hidden;">
        <div data-fit style="overflow:hidden;">
          ${v.store?`<div style="font-family:${FC};font-size:${Math.max(8,C.store-1)}px;font-weight:400;font-style:normal;letter-spacing:3px;text-transform:uppercase;color:#888;margin-bottom:${Math.round(3*s)}px;">${v.store}</div>`:''}
          ${v.price?`<div style="display:flex;align-items:baseline;flex-wrap:wrap;gap:2px;line-height:1;margin-bottom:${Math.round(3*s)}px;">${priceBlock(v,s,C.price,FC,FO)}</div>`:''}
          <div data-title style="font-family:${FC};font-size:${nameFs}px;font-weight:400;font-style:normal;line-height:1.2;overflow-wrap:anywhere;word-break:break-word;">${v.name||'Product Name'}</div>
          ${C.showDesc && v.desc?`<div data-desc style="font-family:open-sans,sans-serif;font-size:${C.desc}px;color:#222;font-weight:600;margin-top:${Math.round(2*s)}px;line-height:1.4;">${v.desc}</div>`:''}
          ${C.showExtra ? extraRow(v.ex1k,v.ex1v,v.ex2k,v.ex2v,C.extra,p,FO) : ''}
        </div>
        ${v.sku?`<div style="border-top:1px solid #ddd;padding-top:${Math.round(4*s)}px;">
          ${code39Barcode(v.sku,barcodeW,barH)}
          <div style="font-family:${FC};font-size:${C.sku}px;font-weight:400;font-style:normal;color:#333;letter-spacing:2px;margin-top:2px;">${v.sku}</div>
        </div>`:''}
      </div>

      ${slot?`<div style="flex-shrink:0;display:flex;flex-direction:column;justify-content:flex-end;">${qrAbs(qrSrc,qrPx,FO,v.direction)}</div>`:''}

    </div>

  </div>`;
}

function tmplMinimal(v: LabelData, px: number, py: number, s: number, _f: Scale, p: number, qrSrc: string | null, qrPx: number, FC: string, FO: string, barcodeW: number, C: TypeSizes): string {
  const nameFs = nameSizeFor(v, C);
  const slot  = _hasSlot(qrSrc, v.direction);
  const barH  = Math.round(24*s);

  return `<div style="position:relative;width:${px}px;height:${py}px;background:#fff;color:#000;font-family:${FO};box-sizing:border-box;border:1px solid #999;overflow:hidden;">

    <div style="position:absolute;top:0;left:0;right:0;bottom:0;display:flex;align-items:stretch;gap:${p}px;padding:${p}px;overflow:hidden;">

      <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:space-between;gap:${Math.round(6*s)}px;overflow:hidden;">
        <div data-fit style="overflow:hidden;">
          ${v.store?`<div style="font-family:${FC};font-size:${Math.max(8,C.store-2)}px;font-weight:400;font-style:normal;letter-spacing:3px;text-transform:uppercase;color:#999;margin-bottom:${Math.round(4*s)}px;">${v.store}</div>`:''}
          <div data-title style="font-family:${FC};font-size:${nameFs}px;font-weight:400;font-style:normal;line-height:1.15;overflow-wrap:anywhere;word-break:break-word;">${v.name||'Product Name'}</div>
          ${C.showDesc && v.desc?`<div data-desc style="font-family:open-sans,sans-serif;font-size:${C.desc}px;color:#222;font-weight:600;margin-top:${Math.round(3*s)}px;line-height:1.4;">${v.desc}</div>`:''}
          ${C.showExtra ? extraRow(v.ex1k,v.ex1v,v.ex2k,v.ex2v,C.extra,p,FO) : ''}
        </div>
        <div>
          ${v.price?`<div style="display:flex;align-items:baseline;flex-wrap:wrap;gap:2px;line-height:1;margin-bottom:${Math.round(2*s)}px;">${priceBlock(v,s,C.price,FC,FO)}</div>`:''}
          ${v.sku?`<div>${code39Barcode(v.sku,barcodeW,barH)}</div>`:''}
          ${v.sku?`<div style="font-family:${FC};font-size:${C.sku}px;font-weight:400;font-style:normal;color:#333;letter-spacing:2px;margin-top:2px;">${v.sku}</div>`:''}
        </div>
      </div>

      ${slot?`<div style="flex-shrink:0;display:flex;flex-direction:column;justify-content:flex-end;">${qrAbs(qrSrc,qrPx,FO,v.direction)}</div>`:''}

    </div>

  </div>`;
}

function tmplRetail(v: LabelData, px: number, py: number, s: number, f: Scale, p: number, qrSrc: string | null, qrPx: number, FC: string, FO: string, barcodeW: number, C: TypeSizes): string {
  const nameFs = nameSizeFor(v, C);
  const headerH = Math.max(17, Math.round(22*s));
  const footerH = Math.max(11, Math.round(16*s));
  const barH    = Math.round(26*s);

  function extraStacked(k1: string, v1: string, k2: string, v2: string){
    if(!k1&&!k2) return '';
    let rows = '';
    if(k1&&v1) rows += `<div style="font-family:${FO};font-size:${C.extra}px;line-height:1.7;"><span style="font-weight:700;color:#222;text-transform:uppercase;letter-spacing:.5px;">${k1}:</span> <span style="font-weight:400;">${v1}</span></div>`;
    if(k2&&v2) rows += `<div style="font-family:${FO};font-size:${C.extra}px;line-height:1.7;"><span style="font-weight:700;color:#222;text-transform:uppercase;letter-spacing:.5px;">${k2}:</span> <span style="font-weight:400;">${v2}</span></div>`;
    return `<div style="margin-top:${Math.round(5*s)}px;">${rows}</div>`;
  }

  const slot     = _hasSlot(qrSrc, v.direction);

  return `<div style="position:relative;width:${px}px;height:${py}px;background:#fff;color:#000;font-family:${FO};border:2px solid #000;box-sizing:border-box;overflow:hidden;">

    <div style="position:absolute;top:0;left:0;right:0;height:${headerH}px;background:#000;color:#fff;display:flex;align-items:center;padding:0 ${p}px;">
      <span style="font-family:${FC};font-size:${C.store}px;font-weight:400;font-style:normal;letter-spacing:${Math.round(3*s)}px;text-transform:uppercase;">${v.store||'STORE NAME'}</span>
    </div>

    <div style="position:absolute;top:${headerH}px;left:0;right:0;bottom:${footerH}px;display:flex;align-items:stretch;gap:${p}px;padding:${p}px;overflow:hidden;">
      <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:space-between;gap:${Math.round(6*s)}px;overflow:hidden;">
        <div data-fit style="overflow:hidden;">
          <div data-title style="font-family:${FC};font-size:${nameFs}px;font-weight:400;font-style:normal;line-height:1.15;overflow-wrap:anywhere;word-break:break-word;margin-bottom:${Math.round(4*s)}px;">${v.name||'Product Name'}</div>
          ${C.showDesc && v.desc?`<div data-desc style="font-family:open-sans,sans-serif;font-size:${C.desc}px;color:#222;font-weight:600;line-height:1.55;">${v.desc}</div>`:''}
          ${C.showExtra ? extraStacked(v.ex1k,v.ex1v,v.ex2k,v.ex2v) : ''}
        </div>
        <div>
          ${v.price?`<div style="display:flex;align-items:baseline;flex-wrap:wrap;gap:2px;line-height:1;margin-bottom:${Math.round(5*s)}px;">${priceBlock(v,s,C.price,FC,FO)}</div>`:''}
          ${v.sku?code39Barcode(v.sku,barcodeW,barH):''}
          ${v.sku?`<div style="font-family:${FC};font-size:${C.sku}px;font-weight:400;font-style:normal;color:#111;letter-spacing:${Math.round(1.5*s)}px;margin-top:${Math.round(4*s)}px;">SKU: ${v.sku}</div>`:''}
        </div>
      </div>
      ${slot?`<div style="flex-shrink:0;display:flex;flex-direction:column;justify-content:flex-end;">${qrAbs(qrSrc,qrPx,FO,v.direction)}</div>`:''}
    </div>

    <div style="position:absolute;bottom:0;left:0;right:0;height:${footerH}px;display:flex;align-items:center;justify-content:flex-end;padding:0 ${p}px;">
      <span style="font-family:${FO};font-size:${f(7)}px;color:#bbb;letter-spacing:1px;">${new Date().toLocaleDateString()}</span>
    </div>

  </div>`;
}

export const LAYOUT_TYPE: Record<string, TypeSizes> = {
  //                price name desc extra sku store showDesc showExtra
  'portrait|1'  : {price:65, name:26, desc:10, extra:11, sku:12, store:12, showDesc:true,  showExtra:true },
  'portrait|2v' : {price:65, name:24, desc:10, extra:11, sku:12, store:11, showDesc:false, showExtra:false},
  'landscape|1' : {price:65, name:30, desc:10, extra:12, sku:13, store:14, showDesc:true,  showExtra:true },
  'landscape|2h': {price:65, name:22, desc:10, extra:11, sku:12, store:11, showDesc:false, showExtra:false}
};

// Sizes for one label, then the chosen design. (Was buildLabel() reading the page's globals; now the choices come in `o`.)
export function buildLabel(v: LabelData, labelW: number, labelH: number, qrSrc: string | null, _layoutN: number, o: DrawOptions): string {
  const px = labelW, py = labelH;
  const s  = px / 480;
  const f  = (n: number) => Math.max(8, Math.round(n * s));
  const p  = Math.round(10 * s);
  const showSlot = _hasSlot(qrSrc, v.direction);
  // QR size: 37.5% of label height or 22.5% of width, whichever is smaller (qrSize() below is the same formula).
  const qrPx  = showSlot ? Math.round(Math.min(py * 0.375, px * 0.225)) : 0;
  const FC = 'contempora-sans-condensed,sans-serif';
  const FO = 'open-sans,sans-serif';
  // Type sizes for this orientation + layout combination
  const C = LAYOUT_TYPE[o.orient + '|' + o.layout] || LAYOUT_TYPE['portrait|1']!;
  // Barcode width — the space actually left in the content column after
  // padding and the QR/arrow slot, so it can never overflow and clip.
  const qrSlotW  = showSlot ? qrPx + 8 + p : 0;
  const barcodeW = Math.max(60, Math.round((labelW - p*2 - qrSlotW) * 0.95));
  const tmpl  = o.template;
  if(tmpl === 'minimal') return tmplMinimal(v,px,py,s,f,p,qrSrc,qrPx,FC,FO,barcodeW,C);
  if(tmpl === 'price')   return tmplPrice(v,px,py,s,f,p,qrSrc,qrPx,FC,FO,barcodeW,C);
  if(tmpl === 'retail')  return tmplRetail(v,px,py,s,f,p,qrSrc,qrPx,FC,FO,barcodeW,C);
  return tmplClassic(v,px,py,s,f,p,qrSrc,qrPx,FC,FO,barcodeW,C);
}

/* Sticker size in pixels: 384×576 (4×6) or 576×384 (6×4), set by the orientation buttons. */
export function getPageDims(orient: Orientation){
  return orient === 'landscape'
    ? {pageW: STICKER_H, pageH: STICKER_W}   // 576 × 384  (6" × 4")
    : {pageW: STICKER_W, pageH: STICKER_H};  // 384 × 576  (4" × 6")
}

// Size of one label: the page minus margins and gaps, split into cols × rows.
export function getLabelDims(key: string, orient: Orientation): PageDims {
  const [cols, rows] = LAYOUT_GRID[key]!;
  const {pageW, pageH} = getPageDims(orient);
  const labelW = Math.floor((pageW - MARGIN*2 - (cols-1)*GAP) / cols);
  const labelH = Math.floor((pageH - MARGIN*2 - (rows-1)*GAP) / rows);
  return {pageW, pageH, labelW, labelH, cols, rows};
}
